import { useTranslation } from 'react-i18next';
import type { SystemSettingResponse } from '../types/setting';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';

interface SettingCardProps {
  setting: SystemSettingResponse;
  onEdit: (setting: SystemSettingResponse) => void;
  onReset: (setting: SystemSettingResponse) => void;
}

function formatDate(isoString: string | undefined, locale: string): string {
  if (!isoString) return '-';
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(isoString));
}

const DATA_TYPE_I18N_KEY: Record<string, string> = {
  BOOLEAN: 'systemSettings.type.boolean',
  INTEGER: 'systemSettings.type.integer',
  STRING: 'systemSettings.type.string',
  ENUM: 'systemSettings.type.enum',
};

const btnBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-1) var(--spacing-3)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  cursor: 'pointer',
  border: '1px solid var(--color-surface-border)',
  fontFamily: 'var(--font-family-sans)',
};

export function SettingCard({ setting, onEdit, onReset }: SettingCardProps) {
  const { t, i18n } = useTranslation();
  const isAlreadyDefault = setting.isDefault;
  const dataTypeLabel = t(DATA_TYPE_I18N_KEY[setting.dataType] ?? 'systemSettings.type.string');

  return (
    <div
      style={{
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        background: 'var(--color-surface-background)',
        marginBottom: 'var(--spacing-3)',
      }}
      aria-label={`${t('systemSettings.card.label.currentValue')}: ${setting.settingKey}`}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--spacing-3)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flex: 1 }}>
          <code
            style={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-primary)',
              fontWeight: 600,
            }}
          >
            {setting.settingKey}
          </code>
          {isAlreadyDefault && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px var(--spacing-2)',
                borderRadius: 'var(--radius-full)',
                background: 'var(--color-success)',
                color: 'var(--color-text-inverse)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
              }}
              aria-label={t('systemSettings.card.badge.isDefault')}
            >
              {t('systemSettings.card.badge.isDefault')}
            </span>
          )}
        </div>

        {/* Action buttons — ButtonGuard: MANAGER lacks SETTING_WRITE → disabled per #324 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexShrink: 0 }}>
          <ButtonGuard requirePermission="SYSTEM_SETTING_WRITE">
            {({ disabled: guardDisabled, reason: guardReason }) => {
              const isDisabled = isAlreadyDefault || guardDisabled;
              const titleText = isAlreadyDefault
                ? t('systemSettings.card.button.reset.disabled.tooltip')
                : guardReason ?? t('systemSettings.card.button.reset');
              return (
                <button
                  type="button"
                  disabled={isDisabled}
                  title={titleText}
                  aria-label={`${setting.settingKey} ${t('systemSettings.card.button.reset')}`}
                  aria-disabled={isDisabled}
                  onClick={() => !isDisabled && onReset(setting)}
                  style={{
                    ...btnBase,
                    background: isDisabled
                      ? 'var(--color-surface-muted)'
                      : 'var(--color-surface-elevated)',
                    color: isDisabled
                      ? 'var(--color-text-muted)'
                      : 'var(--color-text-secondary)',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.6 : 1,
                  }}
                >
                  {t('systemSettings.card.button.reset')}
                </button>
              );
            }}
          </ButtonGuard>
          <ButtonGuard requirePermission="SYSTEM_SETTING_WRITE">
            {({ disabled: guardDisabled, reason: guardReason }) => (
              <button
                type="button"
                disabled={guardDisabled}
                title={guardReason}
                aria-label={`${setting.settingKey} ${t('systemSettings.card.button.edit')}`}
                aria-disabled={guardDisabled}
                onClick={() => !guardDisabled && onEdit(setting)}
                style={{
                  ...btnBase,
                  background: guardDisabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                  color: guardDisabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                  border: 'none',
                  cursor: guardDisabled ? 'not-allowed' : 'pointer',
                  opacity: guardDisabled ? 0.6 : 1,
                }}
              >
                {t('systemSettings.card.button.edit')}
              </button>
            )}
          </ButtonGuard>
        </div>
      </div>

      {/* Description */}
      {setting.description && (
        <p
          style={{
            marginTop: 'var(--spacing-2)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {setting.description}
        </p>
      )}

      {/* Metadata row */}
      <div
        style={{
          marginTop: 'var(--spacing-3)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
        }}
      >
        <span>
          <strong style={{ color: 'var(--color-text-secondary)' }}>{t('systemSettings.card.label.currentValue')}:</strong>{' '}
          <code style={{ fontFamily: 'var(--font-family-mono)' }}>{setting.value}</code>
        </span>
        <span>
          <strong style={{ color: 'var(--color-text-secondary)' }}>{t('systemSettings.card.label.defaultValue')}:</strong>{' '}
          <code style={{ fontFamily: 'var(--font-family-mono)' }}>{setting.defaultValue}</code>
        </span>
        {setting.dataType === 'INTEGER' &&
          (setting.minValue !== null && setting.minValue !== undefined) &&
          (setting.maxValue !== null && setting.maxValue !== undefined) && (
            <span>
              <strong style={{ color: 'var(--color-text-secondary)' }}>{t('systemSettings.card.label.range')}:</strong>{' '}
              {setting.minValue}~{setting.maxValue}
            </span>
          )}
        {setting.dataType === 'ENUM' && setting.enumValues && (
          <span>
            <strong style={{ color: 'var(--color-text-secondary)' }}>{t('systemSettings.card.label.options')}:</strong>{' '}
            {setting.enumValues.split(',').join(' / ')}
          </span>
        )}
        <span>
          <strong style={{ color: 'var(--color-text-secondary)' }}>{t('systemSettings.card.label.dataType')}:</strong>{' '}
          {dataTypeLabel}
        </span>
        {setting.updatedAt && (
          <span>
            <strong style={{ color: 'var(--color-text-secondary)' }}>{t('systemSettings.card.label.lastChanged')}:</strong>{' '}
            {formatDate(setting.updatedAt, i18n.language)}
            {setting.updatedBy && ` (${setting.updatedBy})`}
          </span>
        )}
      </div>
    </div>
  );
}

/** Skeleton version for the loading state */
export function SettingCardSkeleton() {
  return (
    <div
      aria-hidden="true"
      style={{
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        background: 'var(--color-surface-background)',
        marginBottom: 'var(--spacing-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{
            height: 16,
            width: '40%',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-surface-elevated)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          {[60, 48].map((w, i) => (
            <div
              key={i}
              style={{
                height: 28,
                width: w,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ))}
        </div>
      </div>
      <div
        style={{
          marginTop: 'var(--spacing-2)',
          height: 14,
          width: '60%',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--color-surface-elevated)',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      <div
        style={{
          marginTop: 'var(--spacing-3)',
          display: 'flex',
          gap: 'var(--spacing-3)',
        }}
      >
        {[80, 70, 90].map((w, i) => (
          <div
            key={i}
            style={{
              height: 12,
              width: w,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        ))}
      </div>
    </div>
  );
}
